import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import CustomModal from '../../common/CustomModal';
import CustomButton from '../../../components/CustomButton';
import InputField from '../../../components/ui/InputField';
import NativeSelect from '../../../components/ui/NativeSelect';
import SegmentedTabs from '../../../components/SegmentedTabs';
import { paths } from '../../../routes';
import { useGetProductsQuery } from '../../../api/group/groupAdminApiSlice';
import { useTenantCapabilities } from '../../../hooks/useTenantCapabilities';
import {
    productsByUseCase,
    resolvableUseCases,
    stationLinkableProducts,
} from '../createStation/productUseCases';
import {
    buildAddObjectsRequest,
    freePositions,
    planAddedObjects,
} from '../stationPositions';
import type {
    StationAccessType,
    StationDetailResponse,
} from '../../../api/station/stationApiTypes';
import {
    invalidateInventoryReaders,
    useCreateStationObjectsMutation,
    useUpdateStationMutation,
} from '../../../api/inventory/inventoryApiSlice';
import { readInventoryFailure } from '../../../api/inventory/inventoryErrors';

interface Props {
    open: boolean;
    onClose: () => void;
    station: StationDetailResponse;
}

/**
 * Add a further set of objects, against a further Product, to a station that
 * already exists (#2280 — the second of its two proposed shapes).
 *
 * ── What this closes ───────────────────────────────────────────────────────
 *
 * The creation wizard links **every** object of a station to **one** Product in
 * one pass. Real cabinets are not like that: 10× L, 5× M, 15× S in one locker
 * bank, or a Vonovia shelf holding a drill, a window cleaner and a sack truck.
 * Size and type are product-defining (`product-and-asset-model.md` §2b), so
 * those are three Products over one station.
 *
 * **Nothing in the schema or the write API ever forbade that** —
 * `product_resource_link` is a junction, `asset.group_id` and `asset.depot_id`
 * are independent columns, and `POST /stations/:id/objects` takes a `productId`
 * per call and rejects positions already taken, i.e. it was written to be
 * called more than once for one station. The gap was only ever this screen.
 *
 * ── Why it can also grow the station, and why it asks first ────────────────
 *
 * "Add 5 more compartments" usually means the cabinet has more positions than
 * the operator first entered. Growing `door_count` is safe in the one direction
 * that matters — more positions can strand no booking, which is why
 * `stationCapacityBlockers` lets an increase through unconditionally — but it
 * still changes the station, so the new number is stated and agreed to rather
 * than raised as a side effect of adding objects.
 *
 * ── Why it does NOT touch the buffer ───────────────────────────────────────
 *
 * Held-back positions are subtracted from what customers can book. Quietly
 * spending them to fit a batch would reduce nobody's capacity on paper and
 * everybody's in the cabinet. Handing them back is a capacity decision, made on
 * the station form, with its own guard.
 */
const AddObjectsDialog = ({ open, onClose, station }: Props) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const { data, isLoading: isLoadingProducts, isError: isProductsError, refetch }
        = useGetProductsQuery();
    const { services } = useTenantCapabilities();

    const [createObjects, { isLoading: isCreating }] = useCreateStationObjectsMutation();
    const [updateStation, { isLoading: isGrowing }] = useUpdateStationMutation();

    const [productId, setProductId] = useState('');
    const [count, setCount] = useState('');
    const [naming, setNaming] = useState<'auto' | 'manual'>('auto');
    const [prefix, setPrefix] = useState('');
    const [names, setNames] = useState<string[]>([]);
    const [volume, setVolume] = useState('');
    const [note, setNote] = useState('');
    const [growStation, setGrowStation] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [fieldErrors, setFieldErrors] = useState<string[]>([]);

    /*
     * Seeded when the dialog OPENS, not in an effect watching the station.
     *
     * The save refetches the station, and an effect keyed on it would re-seed
     * the form from that refetch — a second writer racing the first, which is
     * the shape CLAUDE.md documents under "useEffect: Separate Init and
     * Reaction". Opening is a discrete event with exactly one writer.
     */
    useEffect(() => {
        if (!open) return;
        setProductId('');
        setCount('');
        setNaming('auto');
        setPrefix('');
        setNames([]);
        setVolume('');
        setNote('');
        setGrowStation(false);
        setSaveError(null);
        setFieldErrors([]);
    }, [open]);

    /**
     * Ask for the product list again when the operator comes back to the tab.
     *
     * "Produkt in Services anlegen" opens a second tab with its own Redux store,
     * so the new Product's cache invalidation never reaches this one. Same
     * reason and same wiring as `LinkProductStep`.
     */
    useEffect(() => {
        if (!open) return undefined;
        const onFocus = () => { refetch(); };
        window.addEventListener('focus', onFocus);
        return () => window.removeEventListener('focus', onFocus);
    }, [open, refetch]);

    const products = useMemo(
        () => stationLinkableProducts(data ?? [], services),
        [data, services],
    );
    const groupedProducts = useMemo(() => productsByUseCase(products), [products]);

    const labelForUseCase = (useCase: string): string =>
        t(`service_type_selector.${useCase.toLowerCase()}`, { defaultValue: useCase });

    const positionState = useMemo(() => ({
        doorCount: station.doorCount,
        bufferDoorsCount: station.bufferDoorsCount,
        objects: station.objects ?? [],
    }), [station]);

    const free = useMemo(() => freePositions(positionState), [positionState]);

    const parsedCount = /^\d+$/.test(count.trim()) ? Number.parseInt(count.trim(), 10) : null;
    const wanted = parsedCount ?? 0;

    /**
     * Where the objects go, and how far the station has to grow for them.
     *
     * One call, because the two answers are one decision: the positions depend
     * on the new ceiling and the new ceiling depends on which positions are
     * free. Computing them separately is what put objects into the held-back
     * tail — see `planAddedObjects`.
     */
    const plan = useMemo(() => planAddedObjects(positionState, wanted), [positionState, wanted]);
    const targetDoors = plan.doors;
    const missing = plan.addedPositions;

    // Manual names are indexed like the positions, so the array follows the
    // count rather than being rebuilt on every keystroke.
    const nameFor = (index: number) => names[index] ?? '';
    const setNameAt = (index: number, value: string) => {
        const next = [...names];
        next[index] = value;
        setNames(next);
    };

    /*
     * The mechanism to write on each new position.
     *
     * `station.doorMechanism` is what the station's EXISTING positions use. When
     * it is null the station either has no positions yet or its positions
     * disagree — and if it also has a lock controller, guessing `'none'` would
     * write compartments the app can never open. Refuse instead of guessing;
     * "No Interim Hardcoded Defaults" is exactly this case.
     */
    const stationDoorMechanism: StationAccessType =
        station.doorMechanism === 'raspberry_pi' || station.doorMechanism === 'dormakaba'
            ? station.doorMechanism
            : 'none';
    const mechanismUnknown = station.doorMechanism === null && station.hasController;

    const needsGrowth = missing > 0;
    const canSave = !mechanismUnknown
        && productId.trim().length > 0
        && parsedCount !== null
        && parsedCount > 0
        && targetDoors.length === wanted
        && (!needsGrowth || growStation)
        && !isCreating
        && !isGrowing;

    const handleSave = useCallback(async () => {
        // `canSave` already carries the count check, and TypeScript narrows
        // through it — a second `parsedCount === null` here is provably dead.
        if (!canSave) return;
        setSaveError(null);
        setFieldErrors([]);
        try {
            /*
             * Grow first, then write. The objects endpoint rejects a position
             * beyond `door_count`, so the order is not a preference — the batch
             * would be refused wholesale otherwise.
             *
             * Not one transaction, and it cannot be: they are two endpoints. The
             * failure mode is benign in the one direction that can happen — a
             * station one size larger with no objects in the new positions,
             * which the operator sees on this very screen and can simply retry.
             * The reverse (objects written into positions the station does not
             * have) is what the order prevents.
             */
            if (needsGrowth) {
                await updateStation({
                    stationId: station.id,
                    doorCount: plan.newDoorCount,
                }).unwrap();
            }

            await createObjects(buildAddObjectsRequest({
                stationId: station.id,
                productId,
                doors: targetDoors,
                naming,
                namePrefix: prefix,
                names,
                // The station's ACTUAL mechanism, read from the positions it
                // already has (`StationDetailResponse.doorMechanism`). This was
                // a hardcoded `'none'` under a comment claiming it was the
                // station's declared mechanism — it was not, and the dialog had
                // no way to know it. On a raspberry_pi or dormakaba station
                // every added compartment would have been written as manual and
                // could never be opened from the app (`doorAppHandler` rejects
                // an unsupported mechanism with a 400).
                doorMechanism: stationDoorMechanism,
                sharedVolumeCm3: /^\d+$/.test(volume.trim())
                    ? Number.parseInt(volume.trim(), 10)
                    : null,
                sharedNote: note,
            })).unwrap();

            invalidateInventoryReaders(dispatch);
            onClose();
        } catch (error) {
            const failure = readInventoryFailure(error);
            // Each cause gets its own sentence: a position clash, a bad field,
            // a refused capacity change and a backend that never answered need
            // different things from the operator, and one message for all four
            // is what sent three sessions to devtools (#2255, #2258).
            if (failure.unreachable) {
                setSaveError(t('station_detail.objects.error_unreachable'));
            } else if (failure.occupiedDoors.length) {
                setSaveError(t('station_detail.objects.error_doors_taken', {
                    doors: failure.occupiedDoors.join(', '),
                }));
            } else if (failure.fieldErrors.length) {
                setFieldErrors(failure.fieldErrors.map(
                    (entry) => `#${entry.index + 1} ${entry.field}: ${entry.message}`,
                ));
                setSaveError(t('station_detail.objects.error_invalid'));
            } else if (failure.blockers.length) {
                setSaveError(failure.blockers
                    .map((blocker) => t(`station_detail.blockers.${blocker}`, {
                        defaultValue: t('station_detail.blockers.unknown'),
                    }))
                    .join(' '));
            } else {
                setSaveError(failure.message ?? t('station_detail.objects.error_generic'));
            }
        }
    }, [
        canSave, needsGrowth, updateStation, station.id, plan.newDoorCount,
        createObjects, productId, targetDoors, naming, prefix, names,
        volume, note, dispatch, onClose, t,
    ]);

    const selectedUseCase = useMemo(() => {
        const selected = products.find((product) => product.id === productId.trim());
        return selected ? resolvableUseCases(selected)[0] ?? '' : '';
    }, [products, productId]);

    return (
        <CustomModal
            open={open}
            onClose={onClose}
            title={t('station_detail.objects.add_title')}
            description={t('station_detail.objects.add_description', {
                station: station.name,
                free: free.length,
            })}
            saveButton={
                <CustomButton
                    title={t('station_detail.objects.add_submit')}
                    onClick={handleSave}
                    disabled={!canSave}
                    loading={isCreating || isGrowing}
                />
            }
            closeButton={
                <CustomButton
                    title={t('common.cancel')}
                    isFilled={false}
                    onClick={onClose}
                />
            }
        >
            <div className={'flex flex-col gap-4'}>
                <div className={'flex flex-col gap-1'}>
                    <label
                        htmlFor={'station-add-product'}
                        className={'text-sm font-medium text-foreground/60'}
                    >
                        {t('station_detail.objects.product_label')}
                    </label>

                    {isLoadingProducts && (
                        <p className={'text-sm text-foreground/60'}>{t('common.loading')}</p>
                    )}

                    {/* A failed load and an empty catalogue say different things
                        and get different sentences — an empty select that reads
                        "nothing matches" is the absence-shaped failure. */}
                    {!isLoadingProducts && isProductsError && (
                        <p className={'text-sm text-foreground'}>
                            {t('station_create.link.products_error')}
                        </p>
                    )}

                    {!isLoadingProducts && !isProductsError && products.length === 0 && (
                        <p className={'text-sm text-foreground'}>
                            {t('station_create.link.no_products')}
                        </p>
                    )}

                    {!isLoadingProducts && !isProductsError && products.length > 0 && (
                        <NativeSelect
                            id={'station-add-product'}
                            value={productId}
                            onChange={(e) => setProductId(e.target.value)}
                        >
                            <option value={''}>
                                {t('station_create.link.product_placeholder')}
                            </option>
                            {groupedProducts.map(([useCase, items]) => (
                                <optgroup key={useCase} label={labelForUseCase(useCase)}>
                                    {items.map((product) => (
                                        <option key={product.id} value={product.id}>
                                            {product.name ?? product.id}
                                        </option>
                                    ))}
                                </optgroup>
                            ))}
                        </NativeSelect>
                    )}

                    {selectedUseCase && (
                        <p className={'mt-1 text-sm text-foreground/60'}>
                            {t('station_create.link.use_case_note', {
                                useCase: labelForUseCase(selectedUseCase),
                            })}
                        </p>
                    )}
                </div>

                <InputField
                    labelAbove
                    type={'number'}
                    integerOnly
                    min={1}
                    label={t('station_detail.objects.count_label')}
                    value={count}
                    onChange={setCount}
                />

                {/* What this will actually do, before it is done — the positions
                    by number, because "5 objects" and "positions 11–15" are
                    different facts and only the second is checkable. */}
                {targetDoors.length > 0 && (
                    <p className={'text-sm text-foreground/60'}>
                        {t('station_detail.objects.positions_preview', {
                            positions: targetDoors.join(', '),
                        })}
                    </p>
                )}

                {needsGrowth && (
                    <div className={'rounded-[5px] bg-foreground/5 px-4 py-3'}>
                        <p className={'text-sm text-foreground'}>
                            {t('station_detail.objects.needs_growth', {
                                free: free.length,
                                missing,
                                newCount: plan.newDoorCount,
                            })}
                        </p>
                        <label className={'mt-2 flex items-center gap-2 text-sm text-foreground'}>
                            <input
                                type={'checkbox'}
                                className={'accent-primary'}
                                checked={growStation}
                                onChange={(e) => setGrowStation(e.target.checked)}
                            />
                            {t('station_detail.objects.grow_confirm', {
                                newCount: plan.newDoorCount,
                            })}
                        </label>
                    </div>
                )}

                <SegmentedTabs<'auto' | 'manual'>
                    items={[
                        { key: 'auto', label: t('station_create.objects.naming_auto') },
                        { key: 'manual', label: t('station_create.objects.naming_manual') },
                    ]}
                    value={naming}
                    onChange={setNaming}
                />

                {naming === 'auto' ? (
                    <div className={'flex flex-col gap-3'}>
                        <InputField
                            labelAbove
                            label={t('station_create.objects.prefix_label')}
                            placeholder={t('station_create.objects.prefix_placeholder')}
                            value={prefix}
                            onChange={setPrefix}
                        />
                        {/* Described ONCE: in auto mode the objects are alike by
                            definition. Entering the same volume ten times was
                            the staging complaint the wizard already fixed, and
                            this screen must not reintroduce it. */}
                        <p className={'text-sm text-foreground/60'}>
                            {t('station_create.objects.shared_details_hint', { count: wanted })}
                        </p>
                        <div className={'grid grid-cols-1 gap-3 sm:grid-cols-2'}>
                            <InputField
                                labelAbove
                                type={'number'}
                                integerOnly
                                min={0}
                                label={t('station_create.objects.shared_volume_label')}
                                placeholder={t('station_create.objects.optional')}
                                value={volume}
                                onChange={setVolume}
                            />
                            <InputField
                                labelAbove
                                label={t('station_create.objects.shared_note_label')}
                                placeholder={t('station_create.objects.optional')}
                                value={note}
                                onChange={setNote}
                            />
                        </div>
                    </div>
                ) : (
                    <div className={'flex flex-col gap-2'}>
                        {targetDoors.map((door, index) => (
                            <InputField
                                key={door}
                                labelAbove
                                label={t('station_detail.objects.name_at_label', { door })}
                                value={nameFor(index)}
                                onChange={(value) => setNameAt(index, value)}
                            />
                        ))}
                    </div>
                )}

                {mechanismUnknown && (
                    <p className={'text-sm text-red-500'}>
                        {t('station_detail.objects.mechanism_unknown')}
                    </p>
                )}

                {fieldErrors.length > 0 && (
                    <ul className={'list-disc pl-5 text-sm text-red-500'}>
                        {fieldErrors.map((message) => <li key={message}>{message}</li>)}
                    </ul>
                )}
                {saveError && <p className={'text-sm text-red-500'}>{saveError}</p>}

                <p className={'text-sm text-foreground/60'}>
                    {t('station_create.link.create_in_services_hint')}{' '}
                    <a
                        href={paths.rentalProducts}
                        target={'_blank'}
                        rel={'noopener noreferrer'}
                        className={'text-primary hover:underline'}
                    >
                        {t('station_create.link.create_in_services_link')}
                    </a>
                </p>
            </div>
        </CustomModal>
    );
};

export default AddObjectsDialog;
